/**
 * Admin Organizations & Membership page
 *
 * Place ANY user into ANY organization or team. Admin-scoped — bypasses the
 * per-org permission checks the normal team/org APIs enforce. Adding a user to
 * a team auto-adds the org membership (team ⊆ org invariant).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { RefreshCw, Plus, Trash2, ArrowLeft, Users, Building2 } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';

export const Route = createFileRoute('/admin/organizations')({
  component: AdminOrganizations,
});

interface OrgRow { id: string; name: string; slug: string; memberCount: number; teamCount: number }
interface Member { userId: string; email: string; displayName: string | null; role: string; joinedAt: string }
interface TeamDetail { id: string; name: string; members: Member[] }
interface OrgDetail { id: string; name: string; slug: string; members: Member[]; teams: TeamDetail[] }

const ORG_ROLES = ['OWNER', 'ADMIN', 'MEMBER', 'VIEWER'];
const TEAM_ROLES = ['LEAD', 'MEMBER', 'VIEWER'];

function AdminOrganizations() {
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadOrgs() {
    setLoading(true);
    try {
      const r = await api.get('/admin/organizations');
      setOrgs(r.data.organizations);
    } catch (e) { console.error('Failed to load orgs', e); }
    finally { setLoading(false); }
  }

  async function loadDetail(orgId: string) {
    try {
      const r = await api.get(`/admin/organizations/${orgId}`);
      setDetail(r.data.organization);
    } catch (e) { console.error('Failed to load org detail', e); }
  }

  useEffect(() => { loadOrgs(); }, []);
  useEffect(() => { if (selectedOrgId) loadDetail(selectedOrgId); else setDetail(null); }, [selectedOrgId]);

  function reportError(err: any, fallback: string) {
    setError(err?.response?.data?.error || err?.message || fallback);
  }

  // ── master list ──────────────────────────────────────────────────────────
  if (!selectedOrgId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Organizations</h1>
            <p className="text-muted-foreground">Manage organizations, teams, and who belongs to them.</p>
          </div>
          <div className="flex gap-2">
            <CreateOrgButton onCreated={loadOrgs} onError={reportError} />
            <Button variant="outline" onClick={loadOrgs} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {error && <ErrorBar msg={error} />}

        <Card>
          <CardHeader><CardTitle>All organizations</CardTitle></CardHeader>
          <CardContent>
            {loading ? <Spinner /> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Teams</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgs.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell className="font-medium">{o.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{o.slug}</TableCell>
                      <TableCell>{o.memberCount}</TableCell>
                      <TableCell>{o.teamCount}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => { setError(null); setSelectedOrgId(o.id); }}>
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {orgs.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No organizations.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── detail view ──────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedOrgId(null); setError(null); loadOrgs(); }}>
          <ArrowLeft className="h-4 w-4 mr-1" /> All orgs
        </Button>
        <h1 className="text-2xl font-bold">{detail?.name ?? 'Loading…'}</h1>
        {detail && <Badge variant="outline">{detail.slug}</Badge>}
      </div>

      {error && <ErrorBar msg={error} />}

      {!detail ? <Spinner /> : (
        <>
          {/* Org members */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Organization members</CardTitle>
              <AddMemberForm
                roles={ORG_ROLES}
                onAdd={async (email, role) => {
                  setError(null);
                  try {
                    await api.post(`/admin/organizations/${detail.id}/members`, { email, role });
                    await loadDetail(detail.id);
                  } catch (e) { reportError(e, 'Failed to add member'); }
                }}
              />
            </CardHeader>
            <CardContent>
              <MemberTable
                members={detail.members}
                onRemove={async (userId) => {
                  if (!confirm('Remove this user from the organization (and all its teams)?')) return;
                  setError(null);
                  try {
                    await api.delete(`/admin/organizations/${detail.id}/members/${userId}`);
                    await loadDetail(detail.id);
                  } catch (e) { reportError(e, 'Failed to remove member'); }
                }}
              />
            </CardContent>
          </Card>

          {/* Teams */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Teams</h2>
            <CreateTeamButton orgId={detail.id} onCreated={() => loadDetail(detail.id)} onError={reportError} />
          </div>

          {detail.teams.map((team) => (
            <Card key={team.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Users className="h-4 w-4" /> {team.name}</CardTitle>
                <AddMemberForm
                  roles={TEAM_ROLES}
                  onAdd={async (email, role) => {
                    setError(null);
                    try {
                      await api.post(`/admin/teams/${team.id}/members`, { email, role });
                      await loadDetail(detail.id);
                    } catch (e) { reportError(e, 'Failed to add team member'); }
                  }}
                />
              </CardHeader>
              <CardContent>
                <MemberTable
                  members={team.members}
                  onRemove={async (userId) => {
                    setError(null);
                    try {
                      await api.delete(`/admin/teams/${team.id}/members/${userId}`);
                      await loadDetail(detail.id);
                    } catch (e) { reportError(e, 'Failed to remove team member'); }
                  }}
                />
              </CardContent>
            </Card>
          ))}
          {detail.teams.length === 0 && (
            <p className="text-muted-foreground text-sm">No teams yet. Create one above.</p>
          )}
        </>
      )}
    </div>
  );
}

// ── small components ─────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center min-h-[160px]">
      <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
    </div>
  );
}

function ErrorBar({ msg }: { msg: string }) {
  return <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">{msg}</div>;
}

function MemberTable({ members, onRemove }: { members: Member[]; onRemove: (userId: string) => void }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Joined</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((m) => (
          <TableRow key={m.userId}>
            <TableCell>
              <div className="font-medium">{m.displayName || '—'}</div>
              <div className="text-xs text-muted-foreground">{m.email}</div>
            </TableCell>
            <TableCell><Badge variant="outline">{m.role}</Badge></TableCell>
            <TableCell className="text-xs text-muted-foreground">{new Date(m.joinedAt).toLocaleDateString()}</TableCell>
            <TableCell className="text-right">
              <Button variant="ghost" size="sm" onClick={() => onRemove(m.userId)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
        {members.length === 0 && (
          <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No members.</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function AddMemberForm({ roles, onAdd }: { roles: string[]; onAdd: (email: string, role: string) => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(roles.includes('MEMBER') ? 'MEMBER' : roles[0]);
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-wrap items-end gap-2 pt-2">
      <div className="flex-1 min-w-[220px]">
        <Label className="text-xs">Add by email</Label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@company.com" />
      </div>
      <div>
        <Label className="text-xs">Role</Label>
        <select className="block mt-1 rounded border bg-background px-3 py-2 text-sm" value={role} onChange={(e) => setRole(e.target.value)}>
          {roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <Button
        size="sm"
        disabled={busy || !email.trim()}
        onClick={async () => { setBusy(true); try { await onAdd(email.trim(), role); setEmail(''); } finally { setBusy(false); } }}
      >
        <Plus className="h-4 w-4 mr-1" /> Add
      </Button>
    </div>
  );
}

function CreateOrgButton({ onCreated, onError }: { onCreated: () => void; onError: (e: any, f: string) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      disabled={busy}
      onClick={async () => {
        const name = prompt('New organization name?');
        if (!name) return;
        setBusy(true);
        try { await api.post('/admin/organizations', { name }); onCreated(); }
        catch (e) { onError(e, 'Failed to create org'); }
        finally { setBusy(false); }
      }}
    >
      <Plus className="h-4 w-4 mr-1" /> New org
    </Button>
  );
}

function CreateTeamButton({ orgId, onCreated, onError }: { orgId: string; onCreated: () => void; onError: (e: any, f: string) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={busy}
      onClick={async () => {
        const name = prompt('New team name?');
        if (!name) return;
        setBusy(true);
        try { await api.post(`/admin/organizations/${orgId}/teams`, { name }); onCreated(); }
        catch (e) { onError(e, 'Failed to create team'); }
        finally { setBusy(false); }
      }}
    >
      <Plus className="h-4 w-4 mr-1" /> New team
    </Button>
  );
}
